/**
 * Fiche de révision d'un pack — route `/pack/$packId/fiche`, enfant direct de `appRoute`
 * (hors layout tuteur/élève : aucune barre latérale à l'impression).
 *
 * Lecture à l'écran + « Imprimer / Enregistrer en PDF », qui ouvre le dialogue d'impression du
 * navigateur : il produit le PDF A4 grâce à la page CSS nommée `@page jr-fiche`. Même technique
 * que l'impression d'une carte 63 × 88 — aucune génération serveur.
 *
 * Paywall : le contenu est filtré par la RPC `get_pack_revision_sheet` (0723). Cette page ne
 * masque rien, elle affiche ce que le serveur a bien voulu envoyer, et propose l'achat du pack
 * quand des cartes manquent.
 *
 * `?student=` : un tuteur lié (ou un admin) peut lire la fiche d'un de ses élèves — telle que
 * l'élève la voit, cartes verrouillées comprises.
 */

import { Link, useParams, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Lock, Printer } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { RevisionSheetView } from '@/features/skillcards/revision-sheet';
import { revisionSheetStats, usePackRevisionSheet } from '@/lib/cards/pack-revision-sheet';

export function PackRevisionSheetPage() {
  const { packId } = useParams({ strict: false }) as { packId: string };
  const { student } = useSearch({ strict: false }) as { student?: string };
  const sheet = usePackRevisionSheet(packId, student ?? null);

  if (sheet.isLoading) return <p className="text-ink-500 p-6 text-sm">Chargement…</p>;
  if (sheet.error) {
    return (
      <p role="alert" className="text-accent-600 p-6 text-sm">
        Cette fiche n’a pas pu être chargée.
      </p>
    );
  }
  if (!sheet.data) {
    return (
      <p role="alert" className="text-ink-500 p-6 text-sm">
        Pack introuvable ou pas encore publié.
      </p>
    );
  }

  const data = sheet.data;
  const stats = revisionSheetStats(data.cards);
  const nothingOpen = stats.unlocked === 0;

  return (
    <div className="jr-fiche-root mx-auto max-w-3xl px-4 py-6 sm:py-8">
      <div className="jr-fiche-hide mb-6 space-y-4">
        <Link
          to="/student/skillcards"
          className="text-ink-500 hover:text-ink-800 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="h-4 w-4" /> Mes SKILLCARDS
        </Link>

        <h1 className="font-display text-ink-900 text-xl font-extrabold">
          Fiche de révision — {data.pack.title}
        </h1>

        {stats.unlocked > 0 && (
          <p className="text-ink-600 text-sm">
            {stats.fragile > 0 ? (
              <>
                <strong>
                  {stats.fragile} carte{stats.fragile > 1 ? 's' : ''} à revoir
                </strong>{' '}
                — repérable
                {stats.fragile > 1 ? 's' : ''} au filet dans la marge.{' '}
              </>
            ) : null}
            {stats.mastered} acquise{stats.mastered > 1 ? 's' : ''}, {stats.untouched} pas encore
            travaillée{stats.untouched > 1 ? 's' : ''}.
          </p>
        )}

        {stats.locked > 0 && (
          <div className="border-ink-200 bg-ink-50 space-y-2 rounded-xl border p-3">
            <p className="text-ink-700 flex items-start gap-2 text-sm">
              <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                {nothingOpen ? (
                  <>
                    Les {stats.locked} cartes de ce pack ne sont pas encore débloquées : la fiche
                    n’affiche que leurs titres.
                  </>
                ) : (
                  <>
                    {stats.locked} carte{stats.locked > 1 ? 's' : ''} de ce pack ne{' '}
                    {stats.locked > 1 ? 'sont' : 'est'} pas débloquée
                    {stats.locked > 1 ? 's' : ''} : elle{stats.locked > 1 ? 's' : ''} apparaî
                    {stats.locked > 1 ? 'ssent' : 't'} sans leur contenu.
                  </>
                )}
              </span>
            </p>
            <Link to="/pack/$packId/acheter" params={{ packId }} search={{ return: undefined }}>
              <Button fullWidth className="sm:w-auto">
                Débloquer le pack complet
              </Button>
            </Link>
          </div>
        )}

        {!nothingOpen && (
          <>
            <p className="text-ink-500 text-sm">
              Dans le dialogue d’impression : « Enregistrer en PDF », format A4, échelle 100 %.
            </p>
            <Button fullWidth className="sm:w-auto" onClick={() => window.print()}>
              <span className="flex items-center justify-center gap-2">
                <Printer className="h-4 w-4" /> Imprimer / Enregistrer en PDF
              </span>
            </Button>
          </>
        )}
      </div>

      <RevisionSheetView sheet={data} />
    </div>
  );
}
